import { Money } from 'gportal-grpc/gportal/type/v1/money_pb';
import type { StrapiPrice } from '#variant/apollo/types/types';

export type MoneyLike = {
  amount: string | number;
  fraction: number;
  currencyCode: string;
};

export type MoneyFormatParam = Omit<Money, 'display'> | MoneyLike;

export default () => {
  // TV-1999: `useI18n()` throws without a component instance, and this composable
  // is called from `store/support.ts`'s setup. `$i18n` is the same composer while
  // vue-i18n is in composition mode.
  const { locale } = useNuxtApp().$i18n;

  /**
   * Formats the amount with the currency depending on the current locale.
   * Works with both proto Money and plain TS objects.
   * @param wallet - money object with amount, fraction and currencyCode
   * @returns the formatted string
   */
  const format = (wallet: MoneyFormatParam): string =>
    Intl.NumberFormat(locale.value, {
      minimumFractionDigits: wallet.fraction,
      style: 'currency',
      currency: wallet.currencyCode,
    }).format(Number(wallet.amount) / 10 ** wallet.fraction);

  const formatStrapiPrice = (price: StrapiPrice, discount = 0.0): string => {
    const percentage = discount === 0 ? 1 : 1 - discount;
    const fraction = price.currency === 'JPY' ? 0 : 2;
    const amount = price.value * percentage;

    return Intl.NumberFormat(locale.value, {
      minimumFractionDigits: fraction,
      style: 'currency',
      currency: price.currency,
    }).format(Number(amount) / 10 ** fraction);
  };

  /**
   * Applies a discount to the native price embedded in a Strapi `formatted`
   * string and returns it in that same native currency.
   *
   * For fallback-currency locations Strapi pre-formats the price as
   * "<native> (~<converted>)" (e.g. "€8.00 (~£6.50)"), and the `StrapiPrice`'s
   * `value`/`currency` hold the *converted* estimate — not the native amount.
   * To keep the discounted price in the native currency we re-derive it from
   * the display string, which Strapi formats with this same locale.
   *
   * The amount is parsed locale-aware (so comma-decimal locales such as
   * "8,00 €" are not mis-read as 800), the discount is applied, and the new
   * amount is spliced back in — preserving the original currency symbol and its
   * placement. This avoids guessing the ISO currency from the symbol, so it
   * works for every currency Strapi may serve, not a fixed allowlist.
   *
   * The numeric token is located as the span from the first digit to the last
   * rather than by matching a list of known grouping characters. Strapi formats
   * the string server-side, so its CLDR data can disagree with the browser's
   * about which separator a locale uses — de-CH grouping, for instance, is
   * U+2019 in older CLDR and U+0027 in newer. Bounding the token by digits
   * keeps the splice correct whichever separator turns up; an unrecognised one
   * used to leave the token unmatched and duplicate the amount, rendering
   * "CHF 1'234.50" as "CHF 1'111.05'234.50".
   *
   * Returns null when no numeric amount can be parsed, letting callers fall
   * back to {@link formatStrapiPrice}.
   */
  const discountNativeStrapiPrice = (
    formatted: string,
    discount = 0.0,
  ): string | null => {
    const nativeDisplay = formatted.split(' (~')[0];

    // Span from the first digit to the last one — everything in between is
    // grouping/decimal punctuation, whatever characters the formatter chose.
    const start = nativeDisplay.search(/[0-9]/);
    if (start === -1) return null;
    const end = nativeDisplay.search(/[0-9](?=[^0-9]*$)/);
    const numericToken = nativeDisplay.slice(start, end + 1);

    const decimalSeparator =
      Intl.NumberFormat(locale.value)
        .formatToParts(1.1)
        .find((part) => part.type === 'decimal')?.value ?? '.';

    // Keep only digits and the locale decimal separator (dropping the group
    // separators), then normalise to a parseable number.
    const decimalEscaped = decimalSeparator.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );
    const normalized = numericToken
      .replace(new RegExp(`[^0-9${decimalEscaped}]`, 'g'), '')
      .replace(decimalSeparator, '.');

    const amount = parseFloat(normalized);
    if (!Number.isFinite(amount)) return null;

    // Preserve the rendered precision as the *minimum* (so "€8.00" stays
    // "€6.80"), but never clamp the maximum below the 2-decimal minor unit
    // most currencies use. Deriving the max from the rendered digit count
    // would silently drop cents when the native amount happens to render
    // without decimals (e.g. "19 kr" → 19 × 0.85 = 16.15 rounded to "16").
    const renderedFractionDigits = normalized.split('.')[1]?.length ?? 0;
    const discounted = Intl.NumberFormat(locale.value, {
      minimumFractionDigits: renderedFractionDigits,
      maximumFractionDigits: Math.max(renderedFractionDigits, 2),
    }).format(amount * (1 - discount));

    // Replace the numeric token only, keeping the currency symbol/placement.
    return (
      nativeDisplay.slice(0, start) + discounted + nativeDisplay.slice(end + 1)
    );
  };

  return {
    format,
    formatStrapiPrice,
    discountNativeStrapiPrice,
  };
};
